import clsx from 'clsx'
import { AnimatePresence, motion, type Variants } from 'motion/react'
import { useSteleBreakpoint } from '@/src/hooks/useBreakpoint'
import { containerVariants, introModalBackdropVariants, introModalCardVariants, tabButtonVariants, tabContainerVariants } from '@/src/lib/motionVariants'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ButtonIcon from '../ui/buttons/ButtonIcon'
import TextHeadline from '../ui/texts/TextHeadline'
import Button from '../ui/buttons/Button'
import IconChevron from '../ui/icons/IconChevron'
import { playButtonPrimarySound, playButtonSecondarySound, playPopupCloseSound } from '@/src/game/audio/uiSounds'
import TabControls from '../ui/controls/TabControls'
import { getTutorialVideoSources } from '@/src/utils/preloadTutorialVideos'

type IntroModalSlide = {
    id: string
    title_1?: string
    title_2?: string
    body: string
    videoSrc: string;
}

type IntroScreenProps = {
    onNext: () => void
    onReturnToConfigurator?: () => void
}

const ctaVariants: Variants = {
    hidden: {
        opacity: 0,
        y: 150,
    },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            type: 'spring',
            stiffness: 400,
            damping: 12,
            mass: 0.8,
        },
    },
}

const introSlideCopyVariants: Variants = {
    hidden: {
        opacity: 0,
        y: 12,
    },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.24,
            ease: 'easeOut',
        },
    },
    exit: {
        opacity: 0,
        y: -12,
        transition: {
            duration: 0.18,
            ease: 'easeIn',
        },
    },
}

export default function IntroScreen({ onNext, onReturnToConfigurator }: IntroScreenProps) {
    const isStele = useSteleBreakpoint()
    const hasAdvancedRef = useRef(false)
    const videoRefs = useRef<(HTMLVideoElement | null)[]>([])
    const [activeSlideIndex, setActiveSlideIndex] = useState(0)
    const [isIntroModalVisible, setIsIntroModalVisible] = useState(false)
    const [isIntroModalDismissed, setIsIntroModalDismissed] = useState(false)
    const [hasReachedLastSlide, setHasReachedLastSlide] = useState(false)

    const tutorialVideoSources = useMemo(() => getTutorialVideoSources(isStele), [isStele])
    const introSlides = useMemo<IntroModalSlide[]>(() => ([
        {
            id: 'slide-1',
            title_1: 'Lauf in',
            title_2: 'deine Zukunft!',
            body: 'Weiche Hindernissen aus oder spring drüber.',
            videoSrc: tutorialVideoSources[0]
        },
        {
            id: 'slide-2',
            title_1: 'Schnapp dir',
            title_2: 'Power-Ups!',
            body: 'Sie schützen dich und helfen dir.',
            videoSrc: tutorialVideoSources[1]
        },
        {
            id: 'slide-3',
            title_1: 'Sammle',
            title_2: 'Münzen!',
            body: 'Damit schaltest du neue Ausrüstung frei.',
            videoSrc: tutorialVideoSources[2]
        },
        {
            id: 'slide-4',
            title_1: 'Hol dir deine',
            title_2: 'Sammelkarte!',
            body: 'Im Ziel bekommst du eine von 4 limitierten Karten.',
            videoSrc: tutorialVideoSources[3]
        },
    ]), [tutorialVideoSources])

    const handleNext = useCallback(() => {
        if (hasAdvancedRef.current) return

        hasAdvancedRef.current = true
        onNext()
    }, [onNext])

    const handlePreviousSlide = useCallback(() => {
        if (activeSlideIndex === 0) return
        playButtonSecondarySound()
        setActiveSlideIndex((current) => Math.max(current - 1, 0))
    }, [activeSlideIndex])

    const handleNextSlide = useCallback(() => {
        if (activeSlideIndex >= introSlides.length - 1) return
        playButtonSecondarySound()
        setActiveSlideIndex((current) => {
            const nextIndex = Math.min(current + 1, introSlides.length - 1)

            if (nextIndex === introSlides.length - 1) {
                setHasReachedLastSlide(true)
            }

            return nextIndex
        })
    }, [activeSlideIndex, introSlides.length])

    const handleCloseIntroModal = useCallback(() => {
        playPopupCloseSound()
        setIsIntroModalVisible(false)
        setIsIntroModalDismissed(true)
    }, [])

    const handleConfirmIntro = useCallback(() => {
        playButtonPrimarySound()
        handleCloseIntroModal()
        handleNext()
    }, [handleCloseIntroModal, handleNext])

    const handleReturnToConfigurator = useCallback(() => {
        playButtonSecondarySound()
        onReturnToConfigurator?.()
    }, [onReturnToConfigurator])

    const isLastSlide = activeSlideIndex === introSlides.length - 1
    const activeSlide = introSlides[activeSlideIndex]

    useEffect(() => {
        if (isIntroModalDismissed) return
        setIsIntroModalVisible(true)
    }, [isIntroModalDismissed])

    useEffect(() => {
        videoRefs.current.forEach((video, index) => {
            if (!video) return

            if (!isIntroModalVisible) {
                video.pause()
                video.currentTime = 0
                return
            }

            if (index === activeSlideIndex) {
                video.currentTime = 0
                void video.play().catch(() => undefined)
                return
            }

            video.pause()
            video.currentTime = 0
        })
    }, [activeSlideIndex, isIntroModalVisible])

    return (
        <>
            <TabControls variants={tabContainerVariants} position={isStele ? 'leftBottom' : 'left'}>
                <motion.div variants={tabButtonVariants}>
                    <ButtonIcon color='red' icon={<IconChevron width='100%' height='100%' />} onClick={handleReturnToConfigurator} />
                </motion.div>
            </TabControls>

            <motion.div
                variants={containerVariants}
                initial='hidden'
                animate='visible'
                className='absolute inset-0 flex flex-col items-center'
            >
                <AnimatePresence>
                    {isIntroModalVisible ? (
                        <motion.div
                            className='z-500 absolute inset-0 flex justify-center items-center stele:items-center bg-black/65 backdrop-blur-sm p-4 stele:p-80 stele:pt-300 pb-8 stele:pb-120 pointer-events-auto'
                            variants={introModalBackdropVariants}
                            initial='hidden'
                            animate='visible'
                            exit='exit'
                        >
                            <motion.div
                                className={clsx(
                                    'relative flex justify-center items-center rounded-3xl w-full h-full',
                                    'stele:rounded-[96px] '
                                )}
                                variants={introModalCardVariants}
                            >
                                <div className='absolute rounded-3xl stele:rounded-[96px] w-full h-full overflow-hidden '>
                                    <motion.div
                                        className='flex h-full w-full'
                                        animate={{ x: `-${activeSlideIndex * 100}%` }}
                                        transition={{
                                            type: 'spring',
                                            stiffness: 260,
                                            damping: 28,
                                            mass: 0.95,
                                        }}
                                    >
                                        {introSlides.map((slide, index) => (
                                            <div
                                                key={slide.id}
                                                className='relative flex h-full min-w-full shrink-0 items-center justify-center'
                                            >
                                                <div className='relative h-full w-full mx-4 stele:mx-16'>
                                                    <video
                                                        ref={(element) => {
                                                            videoRefs.current[index] = element
                                                        }}
                                                        className='absolute h-full w-full rounded-3xl object-cover object-bottom stele:rounded-[96px]'
                                                        src={slide.videoSrc}
                                                        muted
                                                        playsInline
                                                        loop
                                                        preload='auto'
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </motion.div>
                                </div>
                                <div className='bottom-0 left-0 z-10 absolute w-full'>
                                    <div
                                        className={clsx(
                                            'flex flex-col bg-white-gradient border-white-stroke w-full pattern-background',
                                            ' gap-6 px-4 pt-6 pb-8 border rounded-3xl',
                                            'stele:rounded-[96px] stele:px-16 stele:pb-32 stele:pt-24 stele:gap-12'
                                        )}>
                                        <div
                                            className={clsx(
                                                'left-0 absolute flex justify-center items-center w-full z-50',
                                                '-top-16 stele:-top-66'
                                            )}>
                                            <div className='flex flex-col justify-center items-center'>
                                                <TextHeadline
                                                    fontSize={isStele ? 128 : 32}
                                                    leading={-0.05}
                                                    colorPreset='red'
                                                >
                                                    {activeSlide.title_1 ?? ''}
                                                </TextHeadline>
                                                <TextHeadline
                                                    fontSize={isStele ? 128 : 32}
                                                    leading={1}
                                                    colorPreset='red'
                                                >
                                                    {activeSlide.title_2 ?? ''}
                                                </TextHeadline>
                                            </div>
                                        </div>

                                        <AnimatePresence mode='wait'>
                                            <motion.div
                                                key={activeSlide.id}
                                                variants={introSlideCopyVariants}
                                                initial='hidden'
                                                animate='visible'
                                                exit='exit'
                                                className='flex items-center justify-center'
                                            >
                                                <p
                                                    className={clsx(
                                                        'text-white-stroke-darker font-nunito font-bold text-[16px] text-center leading-[135%] tracking-[-0.04em] z-50',
                                                        'stele:text-[64px] stele:max-w-[800px]',
                                                    )}
                                                >
                                                    {activeSlide.body}
                                                </p>
                                            </motion.div>
                                        </AnimatePresence>

                                        <div className='flex justify-center items-center gap-2.5 stele:gap-10'>
                                            {introSlides.map((slide, index) => (
                                                <div
                                                    key={slide.id}
                                                    className={clsx(
                                                        'rounded-full h-2 transition-all duration-200',
                                                        'stele:h-8',
                                                        index === activeSlideIndex
                                                            ? 'w-4 bg-white-stroke-darker stele:w-16 slide-dot-shadow-inner-darker'
                                                            : 'w-2 bg-white-stroke stele:w-8 slide-dot-shadow-inner',
                                                    )}
                                                />
                                            ))}
                                        </div>

                                        <div
                                            className={clsx(
                                                '-bottom-5 left-0 absolute flex justify-center items-center gap-4 px-12 w-full',
                                                'stele:-bottom-20 stele:gap-16 stele:px-48'
                                            )}>
                                            <ButtonIcon
                                                color='blue'
                                                icon={<IconChevron width={isStele ? '80%' : '25'} height={isStele ? '80%' : '27'} />}
                                                variant='standaloneSmall'
                                                onClick={handlePreviousSlide}
                                                className={clsx(activeSlideIndex === 0 && 'opacity-0')}
                                            />
                                            <Button
                                                type='button'
                                                color='blue'
                                                outlined
                                                onClick={handleConfirmIntro}
                                                disabled={!hasReachedLastSlide}
                                                className={clsx(!hasReachedLastSlide && 'opacity-0 pointer-events-none')}
                                            >
                                                Verstanden
                                            </Button>
                                            <ButtonIcon
                                                color='blue'
                                                icon={<IconChevron width={isStele ? '80%' : '25'} height={isStele ? '80%' : '27'} className='rotate-180' />}
                                                variant='standaloneSmall'
                                                onClick={handleNextSlide}
                                                className={clsx(isLastSlide && 'opacity-0')}
                                            />

                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        </motion.div>
                    ) : null
                    }
                </AnimatePresence >
            </motion.div >
        </>
    )
}
